import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { CreditCard, Calendar, TrendingUp, AlertTriangle, CheckCircle, X, ArrowUpRight, ArrowLeft } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SubscriptionManagementPage() {
  const { user, token, refreshUser, loading: authLoading } = useAuth();
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [canceling, setCanceling] = useState(false);
  const [reactivating, setReactivating] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const fetchSubscription = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/subscription/details`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSubscription(data);
      }
    } catch (err) {
      console.error('Failed to fetch subscription:', err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchSubscription();
  }, [fetchSubscription]);

  const handleCancel = async () => {
    setCanceling(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/api/subscription/cancel`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ reason: cancelReason })
      });

      if (response.ok) {
        const data = await response.json();
        setSuccess(data.message);
        setShowCancelModal(false);
        fetchSubscription();
        if (refreshUser) refreshUser();
      } else {
        const data = await response.json();
        setError(data.detail || 'Failed to cancel subscription');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setCanceling(false);
    }
  };

  const handleReactivate = async () => {
    setReactivating(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/api/subscription/reactivate`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        setSuccess('Subscription reactivated successfully!');
        fetchSubscription();
        if (refreshUser) refreshUser();
      } else {
        const data = await response.json();
        setError(data.detail || 'Failed to reactivate subscription');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setReactivating(false);
    }
  };

  const handleUpgrade = async (newPlanId) => {
    try {
      const response = await fetch(`${API_URL}/api/subscription/upgrade`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          new_plan_id: newPlanId,
          origin_url: window.location.origin
        })
      });

      if (response.ok) {
        const data = await response.json();
        window.location.href = data.checkout_url;
      } else {
        const data = await response.json();
        setError(data.detail || 'Failed to initiate upgrade');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-rose-50 to-amber-50 flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-rose-400 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const statusColors = {
    active: 'bg-green-100 text-green-700',
    canceling: 'bg-yellow-100 text-yellow-700',
    canceled: 'bg-red-100 text-red-700',
    past_due: 'bg-red-100 text-red-700',
    pending: 'bg-gray-100 text-gray-700'
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-rose-50 to-amber-50 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Back to Dashboard Button */}
        <Link 
          to="/dashboard" 
          className="inline-flex items-center text-gray-600 hover:text-gray-800 mb-6 group"
          data-testid="back-to-dashboard"
        >
          <ArrowLeft className="w-5 h-5 mr-2 group-hover:-translate-x-1 transition-transform" />
          Back to Dashboard
        </Link>
        
        <h1 className="text-3xl font-bold text-gray-800 mb-8">Subscription Management</h1>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6 flex items-center">
            <AlertTriangle className="w-5 h-5 mr-2" />
            {error}
          </div>
        )}

        {success && (
          <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg mb-6 flex items-center">
            <CheckCircle className="w-5 h-5 mr-2" />
            {success}
          </div>
        )}

        {/* Current Plan Card */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
          <div className="flex items-start justify-between mb-6">
            <div>
              <h2 className="text-xl font-semibold text-gray-800">Current Plan</h2>
              <p className="text-3xl font-bold text-rose-500 mt-2">
                {subscription?.plan_name || 'No Active Plan'}
              </p>
            </div>
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${statusColors[subscription?.subscription_status] || statusColors.pending}`}>
              {subscription?.subscription_status?.replace('_', ' ').toUpperCase() || 'INACTIVE'}
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="flex items-center">
              <CreditCard className="w-10 h-10 text-rose-400 mr-4" />
              <div>
                <p className="text-sm text-gray-500">Monthly Price</p>
                <p className="text-xl font-semibold text-gray-800">${subscription?.price || 0}/mo</p>
              </div>
            </div>

            <div className="flex items-center">
              <TrendingUp className="w-10 h-10 text-rose-400 mr-4" />
              <div>
                <p className="text-sm text-gray-500">Images This Month</p>
                <p className="text-xl font-semibold text-gray-800">
                  {subscription?.images_used || 0} / {subscription?.images_limit || 0}
                </p>
              </div>
            </div>

            <div className="flex items-center">
              <Calendar className="w-10 h-10 text-rose-400 mr-4" />
              <div>
                <p className="text-sm text-gray-500">
                  {subscription?.cancel_at_period_end ? 'Access Until' : 'Next Billing'}
                </p>
                <p className="text-xl font-semibold text-gray-800">
                  {formatDate(subscription?.next_billing_date)}
                </p>
              </div>
            </div>
          </div>

          {/* Usage Progress Bar */}
          <div className="mt-6">
            <div className="flex justify-between text-sm text-gray-600 mb-2">
              <span>Usage</span>
              <span>{subscription?.images_remaining || 0} images remaining</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3">
              <div
                className="bg-gradient-to-r from-rose-400 to-orange-400 h-3 rounded-full transition-all"
                style={{
                  width: `${Math.min(100, ((subscription?.images_used || 0) / (subscription?.images_limit || 1)) * 100)}%`
                }}
              />
            </div>
          </div>

          {/* Cancellation Warning */}
          {subscription?.cancel_at_period_end && (
            <div className="mt-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-start">
              <AlertTriangle className="w-5 h-5 text-yellow-600 mr-3 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-yellow-800">Subscription Cancellation Pending</p>
                <p className="text-sm text-yellow-700 mt-1">
                  Your subscription will end on {formatDate(subscription?.next_billing_date)}. You'll retain access until then.
                </p>
                <button
                  onClick={handleReactivate}
                  disabled={reactivating}
                  className="mt-3 text-sm font-medium text-yellow-800 hover:text-yellow-900 underline"
                >
                  {reactivating ? 'Reactivating...' : 'Reactivate Subscription'}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Features */}
        {subscription?.features?.length > 0 && (
          <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Plan Features</h3>
            <ul className="space-y-3">
              {subscription.features.map((feature, index) => (
                <li key={index} className="flex items-center text-gray-700">
                  <CheckCircle className="w-5 h-5 text-green-500 mr-3" />
                  {feature}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Upgrade Options */}
        {subscription?.available_upgrades?.length > 0 && (
          <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Upgrade Your Plan</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {subscription.available_upgrades.map((upgrade) => (
                <div
                  key={upgrade.plan_id}
                  className="border border-gray-200 rounded-xl p-4 hover:border-rose-400 transition-colors"
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <h4 className="font-semibold text-gray-800">{upgrade.name}</h4>
                      <p className="text-2xl font-bold text-rose-500 mt-1">${upgrade.price}/mo</p>
                    </div>
                    <button
                      onClick={() => handleUpgrade(upgrade.plan_id)}
                      className="flex items-center px-4 py-2 bg-gradient-to-r from-rose-400 to-orange-400 text-white rounded-lg text-sm font-medium hover:shadow-lg transition-all"
                    >
                      Upgrade <ArrowUpRight className="w-4 h-4 ml-1" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Cancel Subscription */}
        {subscription?.subscription_status === 'active' && !subscription?.cancel_at_period_end && (
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-2">Cancel Subscription</h3>
            <p className="text-gray-600 mb-4">
              We'd be sad to see you go! If you cancel, you'll retain access until the end of your billing period.
            </p>
            <button
              onClick={() => setShowCancelModal(true)}
              className="text-red-500 hover:text-red-600 font-medium"
              data-testid="cancel-subscription-btn"
            >
              Cancel Subscription
            </button>
          </div>
        )}

        {/* Cancel Modal */}
        {showCancelModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold text-gray-800">Cancel Subscription</h2>
                <button onClick={() => setShowCancelModal(false)} className="p-2 hover:bg-gray-100 rounded-full">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <p className="text-gray-600 mb-4">
                Are you sure you want to cancel? You'll lose access to:
              </p>
              <ul className="mb-6 space-y-2">
                <li className="flex items-center text-gray-700">
                  <X className="w-4 h-4 text-red-500 mr-2" />
                  {subscription?.images_limit} AI try-on images per month
                </li>
                <li className="flex items-center text-gray-700">
                  <X className="w-4 h-4 text-red-500 mr-2" />
                  {user?.user_type === 'brand' ? 'Portfolio management' : 'Affiliate earnings'}
                </li>
              </ul>

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Help us improve - why are you canceling? (optional)
                </label>
                <textarea
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  rows={3}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-400 focus:border-transparent"
                  placeholder="Your feedback helps us improve..."
                />
              </div>

              <div className="flex gap-4">
                <button
                  onClick={() => setShowCancelModal(false)}
                  className="flex-1 py-3 border border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition-colors"
                >
                  Keep Subscription
                </button>
                <button
                  onClick={handleCancel}
                  disabled={canceling}
                  className="flex-1 py-3 bg-red-500 text-white rounded-lg font-semibold hover:bg-red-600 transition-colors disabled:opacity-50"
                  data-testid="confirm-cancel-btn"
                >
                  {canceling ? 'Canceling...' : 'Yes, Cancel'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
